import { useCallback, useContext, useEffect, useRef, useState } from "react";

import Cancel from "@mui/icons-material/Cancel";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useSessionStorage } from "@react-hooks-library/core";
import { SubmissionSummary } from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useDrawer } from "@/common/SlideoutDrawer/DrawerContext";
import SlideOutDrawer from "@/common/SlideoutDrawer/SlideoutDrawer";
import { mailTo } from "@/common/utils";
import { DisabledComponentDialog } from "@/components/DisabledComponent";
import { parseUserPermissions } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useRegisterAutoRefresh } from "@/contexts/AutoRefreshContext";
import { useDevEnvironment } from "@/contexts/DevContext";
import { useImageMatch } from "@/contexts/ImageMatchContext";
import {
  PersistentFilterValidators,
  usePersistentFilters,
} from "@/hooks/usePersistentFilters";
import { useRegionFilterOptions } from "@/hooks/useRegionFilterOptions";
import { IUserPermission } from "@/types/typesAlerts";
import {
  getStatusOptions,
  ReportStatus,
  reportColourTypes,
  reportStatusTooltips,
  statusTypes,
} from "@/types/typesReportV2";

import { OnlineReportingContext } from "./context";
import CondensedReportView from "./report-view/CondensedReportView";
import { getReportTitle } from "./report-view/utils";
import ReportCard from "./ReportCard";
import ReportsMap from "./ReportsMap";
import style from "./ReportV2List.module.scss";
import ReportFilters, {
  IReportListFilterOptions,
  RenderReportFilterChips,
} from "./Search/ReportFilters";
import SubmissionListView from "./SubmissionListView";
import {
  DEFAULT_SUBMISSION_SORT,
  fromGridSortModel,
  SubmissionSort,
  SUBMISSION_SORT_FIELDS,
  sortableDateColumn,
  sortFromFilters,
  toGridSortModel,
} from "./submissionSort";
import { assignedTo, lastUpdatedBy } from "./utils";

/**
 * Creates columns for the DataGrid and applies default settings
 * @param fields
 * @returns
 */
const createColumns = (fields: GridColDef[]) => {
  return fields.map((f: GridColDef) => {
    const field: GridColDef = {
      sortable: false,
      ...f,
    };
    return field;
  });
};

const parseAddress = (
  address: string,
): {
  streetName: string;
  city: string;
} => {
  if (!address) {
    return { streetName: "", city: "" };
  }

  const parts = address.split(",").map((part) => part.trim());

  if (parts.length < 2) {
    return { streetName: address, city: "" };
  }

  return { streetName: parts[0], city: parts[1] };
};

const generateRowData = (submission: SubmissionSummary) => {
  const latestData = submission.latest_data as
    | Record<string, unknown>
    | undefined;
  const incidentLocationAddress = latestData?.incident_location_address as
    | {
        formatted_address?: string;
        googleMapsPlace?: { formatted_address?: string };
      }
    | undefined;
  const address =
    incidentLocationAddress?.formatted_address ||
    incidentLocationAddress?.googleMapsPlace?.formatted_address ||
    (latestData?.incident_location_highway as string) ||
    "";

  const { streetName, city } = parseAddress(address);
  const alternateCity = latestData?.incident_location_highwayCity;

  const rowData = {
    ...submission,
    survey_name: getReportTitle(
      submission.is_supplemental,
      submission.survey_name ?? "",
    ),
    time_updated: new Date(submission.time_updated),
    time_created: new Date(submission.time_created),
    time_submitted: submission.time_submitted
      ? new Date(submission.time_submitted)
      : new Date(submission.time_created),
    tracking_number: `${submission.tracking_number ?? ""} v${
      submission.latest_version
    }`,
    updated_by: lastUpdatedBy(submission),
    assigned_to: assignedTo(submission),
    name: `${submission.first_name ?? ""} ${submission.last_name ?? ""}`.trim(),
    address,
    street_name: streetName,
    city: city ?? alternateCity ?? "",
  };
  return rowData;
};

const ReportV2List = ({ cisoDisplay }: { cisoDisplay: boolean }) => {
  const {
    reports,
    reportTypeNames,
    totalReportsCount,
    fetchLatestReports,
    fetchReportConfigurations,
    searchText,
    setSearchText,
    filtersVisible,
    setFiltersVisible,
    searchView,
    setSearchView,
    reportTablePage,
    setReportTablePage,
    rv2Users,
    fetchRv2Users,
    imageSearchID,
    imageAttachmentId,
    imageSubmissionId,
  } = useContext(OnlineReportingContext);
  const { currentUser } = useContext(AuthStateContext);
  const { isOpen, openDrawer, closeDrawer, setDrawerWidth } = useDrawer();
  const { setNavigateToReport } = useImageMatch();
  // Read pinned submission synchronously from sessionStorage for initialization
  const storedPinned = (() => {
    try {
      const raw = sessionStorage.getItem("pinnedSubmission");
      return raw ? (JSON.parse(raw) as SubmissionSummary) : null;
    } catch {
      return null;
    }
  })();

  const [selectedReport, setSelectedReport] =
    useState<SubmissionSummary | null>(storedPinned);
  const [compareReport, setCompareReport] = useState<SubmissionSummary | null>(
    null,
  );
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const pinToFront = searchParams.get("pinToFront") === "true";

  const setPinnedSubmission = (submission: SubmissionSummary | null) => {
    if (submission) {
      sessionStorage.setItem("pinnedSubmission", JSON.stringify(submission));
    } else {
      sessionStorage.removeItem("pinnedSubmission");
    }
  };

  const { orgId } = useParams();
  const appName = window.location.pathname.split("/")[1];
  const [loadingReports, setLoadingReports] = useState(false);
  // A background refresh keeps the current rows on screen, so it is tracked
  // separately from loadingReports (which swaps the grid for its overlay).
  const [backgroundRefreshing, setBackgroundRefreshing] = useState(false);
  const backgroundRefreshRef = useRef(false);
  const fetchInFlightRef = useRef(false);
  // Set when filters or paging change mid-fetch, so the latest request still runs once it lands.
  const refetchPendingRef = useRef(false);
  const navigate = useNavigate();
  const [filters, setFilters, validateFilters] =
    usePersistentFilters<IReportListFilterOptions>({
      storageKey: "reportFilters",
      orgId,
      defaultValue: {
        creationStartDate: null,
        creationEndDate: null,
        updateStartDate: null,
        updateEndDate: null,
        reportStatus: null,
        trackingNumber: null,
        reportType: null,
        assignedTo: null,
        registries: null,
        image: null,
        imageSimilarity: null,
        sortOrder: DEFAULT_SUBMISSION_SORT.sortOrder,
        sortBy: DEFAULT_SUBMISSION_SORT.sortBy,
        region: null,
      },
      persistentKeys: [
        "reportStatus",
        "reportType",
        "assignedTo",
        "region",
        "sortBy",
        "sortOrder",
      ],
    });

  const {
    options: regionFilterOptions,
    isLoading: regionFilterOptionsLoading,
  } = useRegionFilterOptions();

  // Reset a persisted filter once live option data shows it's no longer valid.
  useEffect(() => {
    const validators: PersistentFilterValidators<IReportListFilterOptions> = {
      reportStatus: (value) =>
        value.split("|").every((status) => getStatusOptions().includes(status)),
      reportType: (value) =>
        reportTypeNames.length === 0 ||
        value.split("|").every((type) => reportTypeNames.includes(type)),
      assignedTo: (value) =>
        value === "unassigned" ||
        value === "anyone" ||
        rv2Users === null ||
        rv2Users.users.some((user) => user.id === value),
      region: (value) =>
        regionFilterOptionsLoading ||
        regionFilterOptions.some((option) => option.value === value),
      sortBy: (value) => SUBMISSION_SORT_FIELDS.includes(value),
      sortOrder: (value) => value === "asc" || value === "desc",
    };
    validateFilters(validators);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reportTypeNames,
    rv2Users,
    regionFilterOptions,
    regionFilterOptionsLoading,
  ]);
  const sort = sortFromFilters(filters);

  const [columnVisibilityModel, setColumnVisibilityModel] = useSessionStorage(
    "reportColumnVisibility",
    {},
  );

  const [paginationModel, setPaginationModel] = useState({
    page: reportTablePage ?? 0,
    pageSize: 8,
  });

  useEffect(() => {
    setNavigateToReport((submissionId: string) => {
      const summary: SubmissionSummary = {
        id: submissionId,
      } as SubmissionSummary;
      setSelectedReport(summary);
      setDrawerWidth(window.innerWidth * 0.5);
      openDrawer();
    });
    return () => setNavigateToReport(null);
  }, [setNavigateToReport, openDrawer, setDrawerWidth]);

  // Restore pinned drawer state on mount
  useEffect(() => {
    if (storedPinned) {
      setSearchParams((prev) => {
        prev.set("pinToFront", "true");
        return prev;
      });
      setDrawerWidth(window.innerWidth * 0.5);
      openDrawer();
    }
  }, []);

  useEffect(() => {
    const page = reportTablePage ?? 0;
    setPaginationModel((prev) =>
      prev.page === page ? prev : { ...prev, page },
    );
  }, [reportTablePage]);

  const handlePageChange = (newPage: number) => {
    setReportTablePage(newPage);
  };

  const { isDevEnv } = useDevEnvironment();
  const [reportPermissions] = useState(
    currentUser &&
      parseUserPermissions(currentUser?.permissions as IUserPermission[]),
  );

  // Check intel permissions when in intel mode (cisoDisplay), otherwise check report_v2 permissions
  const canViewReportsRV2 = reportPermissions
    ? cisoDisplay
      ? reportPermissions.canViewEntriesIntel
      : reportPermissions.canViewReportsRV2
    : true;

  const [debouncedFilters, setDebouncedFilters] =
    useState<IReportListFilterOptions | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [filters]);

  const filtersSettled = debouncedFilters === filters;

  const handleAutoRefresh = useCallback(() => {
    // A load is already running; it restarts the countdown when it lands.
    if (fetchInFlightRef.current) {
      return;
    }
    backgroundRefreshRef.current = true;
    setRefreshNonce((n) => n + 1);
  }, []);

  const { markRefreshed } = useRegisterAutoRefresh({
    onRefresh: handleAutoRefresh,
    enabled: canViewReportsRV2,
    isRefreshing: loadingReports || backgroundRefreshing,
  });

  useEffect(() => {
    const isBackgroundRefresh = backgroundRefreshRef.current;
    backgroundRefreshRef.current = false;

    async function fetchData() {
      fetchInFlightRef.current = true;
      if (isBackgroundRefresh) {
        setBackgroundRefreshing(true);
      } else {
        setLoadingReports(true);
      }

      const settle = (succeeded: boolean) => {
        fetchInFlightRef.current = false;
        setLoadingReports(false);
        setBackgroundRefreshing(false);
        markRefreshed(succeeded);
        if (refetchPendingRef.current) {
          refetchPendingRef.current = false;
          setRefreshNonce((n) => n + 1);
        }
      };

      try {
        await fetchReportConfigurations!(orgId ?? "");
        await fetchRv2Users(orgId ?? "");
        await fetchLatestReports!(
          orgId ?? "",
          debouncedFilters?.creationStartDate ?? undefined,
          debouncedFilters?.creationEndDate ?? undefined,
          debouncedFilters?.updateStartDate ?? undefined,
          debouncedFilters?.updateEndDate ?? undefined,
          debouncedFilters?.reportStatus
            ? decodeURIComponent(debouncedFilters?.reportStatus)
            : undefined,
          debouncedFilters?.trackingNumber ?? undefined,
          paginationModel.pageSize,
          paginationModel.page * paginationModel.pageSize,
          debouncedFilters?.reportType ?? undefined,
          searchText,
          debouncedFilters?.assignedTo ?? undefined,
          debouncedFilters?.registries ?? undefined,
          debouncedFilters?.image ?? undefined,
          debouncedFilters?.imageSimilarity ?? undefined,
          debouncedFilters?.sortOrder,
          debouncedFilters?.region ?? undefined,
          debouncedFilters?.sortBy,
        );
        settle(true);
      } catch (e) {
        settle(false);
        const message = (e as Error).message;
        toast.error(`Failed to fetch reports${isDevEnv ? `: ${message}` : ""}`);
      }
    }
    if (!debouncedFilters || !filtersSettled) return;
    if (fetchInFlightRef.current) {
      refetchPendingRef.current = true;
      return;
    }
    fetchData();
  }, [
    debouncedFilters,
    filtersSettled,
    paginationModel.page,
    paginationModel.pageSize,
    searchText,
    imageSearchID,
    imageAttachmentId,
    imageSubmissionId,
    searchView,
    refreshNonce,
    markRefreshed,
  ]);

  const rows = reports!
    .filter((report) => {
      return (
        (report.latest_version &&
          report.latest_version > 0 &&
          report.is_supplemental === false) ||
        (report.is_supplemental === true && report.is_linked === false)
      );
    })
    .map((report) => {
      return generateRowData(report);
    });

  const fields: GridColDef[] = [
    {
      field: "status",
      headerName: "Status",
      width: 200,
      filterable: false,
      renderCell: (params) => {
        const status = params.value as ReportStatus;

        return (
          <Tooltip title={reportStatusTooltips[status] || ""} arrow>
            <Chip
              label={statusTypes[status] ? statusTypes[status] : status}
              color={
                reportColourTypes[status]
                  ? reportColourTypes[status]
                  : "default"
              }
            />
          </Tooltip>
        );
      },
    },
    {
      field: "assigned_to",
      headerName: "Assigned To",
      width: 130,
      filterable: false,
    },
    {
      field: "incident_number",
      headerName: "RMS Number",
      width: 150,
      sortable: false,
      filterable: false,
    },
    {
      field: "tracking_number",
      headerName: "Tracking Number",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const supplementalCount: number = params.row.supplemental_count;
        const supplementalTrackingNumbers: string[] =
          params.row.supplemental_tracking_numbers ?? [];

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span>{params.value}</span>
            {supplementalCount > 0 && (
              <Tooltip
                arrow
                describeChild
                title={
                  <>
                    <div>
                      {supplementalCount === 1
                        ? "1 linked supplemental"
                        : `${supplementalCount} linked supplementals`}
                    </div>
                    {supplementalTrackingNumbers.map((trackingNumber) => (
                      <div key={trackingNumber}>{trackingNumber}</div>
                    ))}
                  </>
                }
              >
                <Chip
                  label={`+${supplementalCount}`}
                  size="small"
                  variant="outlined"
                  tabIndex={0}
                  sx={{ height: 20, fontSize: "0.7rem" }}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      field: "survey_name",
      headerName: "Report Type",
      width: 200,
      sortable: false,
      filterable: false,
    },
    {
      field: "name",
      headerName: "Name",
      width: 200,
      filterable: false,
    },
    {
      field: "street_name",
      headerName: "Street Name",
      width: 150,
      filterable: false,
    },
    {
      field: "city",
      headerName: "City",
      width: 100,
      filterable: false,
    },
    {
      field: "time_updated",
      headerName: "Time Updated",
      width: 200,
      type: "dateTime",
      ...sortableDateColumn,
      filterable: false,
    },
    {
      field: "time_submitted",
      headerName: "Time Submitted",
      width: 200,
      type: "dateTime",
      ...sortableDateColumn,
      filterable: false,
    },
    {
      width: 200,
      field: "updated_by",
      headerName: "Updated By",
      filterable: false,
    },
  ];

  const columns = createColumns(fields);

  function updateSearch(updatedFilters: IReportListFilterOptions) {
    setReportTablePage(0);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setFilters(updatedFilters);
  }

  const handleSortChange = (next: SubmissionSort) =>
    updateSearch({ ...filters, ...next });

  const handleColumnVisibilityModelChange = (
    newModel: Record<string, boolean>,
  ) => {
    setColumnVisibilityModel(newModel);
  };

  // Card-view infinite scroll anchor (ciso display).
  const bottomPageRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            !loadingReports &&
            reports.length < totalReportsCount
          ) {
            // The API caps the limit, so a short page means growing further
            // would refetch the same rows forever.
            setPaginationModel((prev) =>
              prev.page === 0 && reports.length < prev.pageSize
                ? prev
                : { page: 0, pageSize: prev.pageSize + 8 },
            );
          }
        });
      },
      { threshold: 1 },
    );

    const currentAnchor = bottomPageRef.current;
    if (currentAnchor) {
      observer.observe(currentAnchor);
    }

    return () => {
      if (currentAnchor) {
        observer.unobserve(currentAnchor);
      }
    };
  }, [bottomPageRef, loadingReports, reports.length, totalReportsCount]);

  const handleRowClick = (report: SubmissionSummary) => {
    if (pinToFront && selectedReport) {
      // When pinned, clicking a second card opens it as the compare report
      if (report.id !== selectedReport.id) {
        setCompareReport(report);
        setDrawerWidth(window.innerWidth * 0.7);
        openDrawer();
      }
    } else if (!selectedReport || selectedReport.id !== report.id) {
      setSelectedReport(report);
      setDrawerWidth(window.innerWidth * 0.5);
      openDrawer();
    }
  };

  const handlePinToggle = () => {
    if (pinToFront) {
      // Unpin: clear compare and pinToFront
      setSearchParams((prev) => {
        prev.delete("pinToFront");
        return prev;
      });
      setCompareReport(null);
      setDrawerWidth(window.innerWidth * 0.5);
      setPinnedSubmission(null);
    } else {
      // Pin the current report
      setSearchParams((prev) => {
        prev.set("pinToFront", "true");
        return prev;
      });
      setPinnedSubmission(selectedReport);
    }
  };

  const handleCloseCompare = () => {
    setCompareReport(null);
    setDrawerWidth(window.innerWidth * 0.5);
  };

  const handleSubmissionDeleted = () => {
    setSelectedReport(null);
    setCompareReport(null);
    setSearchParams((prev) => {
      prev.delete("pinToFront");
      return prev;
    });
    setPinnedSubmission(null);
    closeDrawer();
    setRefreshNonce((n) => n + 1);
  };

  const handleCloseMain = () => {
    if (pinToFront && compareReport) {
      // Move compare to main position
      setSelectedReport(compareReport);
      setCompareReport(null);
      setDrawerWidth(window.innerWidth * 0.5);
      setPinnedSubmission(compareReport);
    } else {
      setSelectedReport(null);
      setSearchParams((prev) => {
        prev.delete("pinToFront");
        return prev;
      });
      setCompareReport(null);
      setPinnedSubmission(null);
      closeDrawer();
    }
  };

  const renderCardView = () => {
    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "16px",
        }}
      >
        {reports.length > 0 &&
          reports.map((report, index) => (
            <ReportCard
              key={report.id}
              report={report}
              onClick={() => {
                if (cisoDisplay) {
                  handleRowClick(report);
                } else {
                  navigate(
                    `/${appName}/${orgId}/report/${report.id}/condensed`,
                  );
                }
              }}
              index={index}
              reportType={reports[index]?.survey_name || "Unknown Report Type"}
              className={
                (selectedReport && selectedReport.id === report.id) ||
                (compareReport && compareReport.id === report.id)
                  ? style.selectedCard
                  : ""
              }
            />
          ))}
        <div ref={bottomPageRef}></div>
        {loadingReports && <CircularProgress />}
        {/* Pagination right now just loads more, this needs to be changed to cache previous list items */}
        {/* <Button
          onClick={() => {

          }}
          style={{
            margin: "16px",
            display: "block",
            marginLeft: "auto"
          }}
        >
          Load More
        </Button> */}
      </div>
    );
  };

  const renderMapView = () => {
    return (
      <ReportsMap
        reports={reports}
        reportClickHandler={(report) => {
          if (cisoDisplay) {
            handleRowClick(report);
          } else navigate(`/${appName}/${orgId}/report/${report.id}/condensed`);
        }}
        loadMore={() => {}}
        isFetchingNextPage={false}
        hasNextPage={false}
        totalReports={totalReportsCount}
        selectedReport={selectedReport ?? undefined}
        setSelectedReport={setSelectedReport}
      />
    );
  };

  if (!canViewReportsRV2) {
    return (
      <DisabledComponentDialog
        isDialogOpen={true}
        handleClose={() => {
          navigate(`/`);
        }}
        title="You do not have access to view reports."
        message={
          <Typography>
            Please contact{" "}
            <Link
              rel="noopener noreferrer"
              target="_blank"
              href={mailTo({
                email: "support@tryrubicon.com",
                subject: "Requesting Access to View Reports",
                body: `Hello Rubicon Support Team, 

                I would like to request access to view reports.

                Thank you. 

                Sincerely,

                ${currentUser?.first_name} ${currentUser?.last_name} 
                ${currentUser?.email}`,
              })}
              color="secondary"
            >
              support@tryrubicon.com
            </Link>{" "}
            to request access.
          </Typography>
        }
      />
    );
  }

  return (
    <SlideOutDrawer
      width={240}
      slideoutLocation="right"
      slideoutContent={
        selectedReport ? (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              height: "100%",
              width: "100%",
            }}
          >
            {/* Compare pane (second report, shown on the left when pinned and a second card is clicked) */}
            {pinToFront && compareReport && (
              <>
                <div
                  key={`compare-${compareReport.id}`}
                  style={{
                    flex: 1,
                    overflow: "auto",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      padding: "4px 8px",
                      borderBottom: 1,
                      borderColor: "divider",
                      gap: "4px",
                    }}
                  >
                    <Chip
                      label="Comparison"
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{ mr: "auto" }}
                    />
                    <Tooltip title="Close comparison">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={handleCloseCompare}
                      >
                        <Cancel />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <CondensedReportView
                    cisoDisplay={cisoDisplay}
                    idOverrride={compareReport.id}
                    onSubmissionDeleted={handleSubmissionDeleted}
                  />
                </div>
                <Box
                  sx={{
                    width: "1px",
                    bgcolor: "divider",
                    flexShrink: 0,
                  }}
                />
              </>
            )}

            {/* Main pane (pinned report, on the right) */}
            <div
              key={`main-${selectedReport.id}`}
              style={{
                flex: 1,
                overflow: "auto",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  padding: "4px 8px",
                  bgcolor: pinToFront ? "action.selected" : undefined,
                  borderBottom: 1,
                  borderColor: "divider",
                  gap: "4px",
                }}
              >
                {pinToFront && (
                  <Chip
                    label="Pinned"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mr: "auto" }}
                  />
                )}
                <Tooltip
                  title={
                    pinToFront
                      ? "Unpin submission"
                      : "Pin submission to compare"
                  }
                >
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handlePinToggle}
                  >
                    {pinToFront ? <PushPinIcon /> : <PushPinOutlinedIcon />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleCloseMain}
                  >
                    <Cancel />
                  </IconButton>
                </Tooltip>
              </Box>
              <CondensedReportView
                cisoDisplay={cisoDisplay}
                idOverrride={selectedReport.id}
                onSubmissionDeleted={handleSubmissionDeleted}
              />
            </div>
          </div>
        ) : (
          <p>Select a report to see details.</p>
        )
      }
      mainContent={
        <SubmissionListView
          rows={rows}
          columns={columns}
          rowCount={totalReportsCount}
          loading={loadingReports}
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => {
            handlePageChange(model.page);
            setPaginationModel(model);
          }}
          searchText={searchText}
          setSearchText={setSearchText}
          sort={sort}
          onSortChange={handleSortChange}
          sortModel={toGridSortModel(sort)}
          onSortModelChange={(model) =>
            handleSortChange(fromGridSortModel(model))
          }
          filtersVisible={filtersVisible}
          setFiltersVisible={setFiltersVisible}
          filterChips={
            <RenderReportFilterChips
              filters={filters}
              setFilters={updateSearch}
            />
          }
          renderFilterPanel={(anchorEl) => (
            <ReportFilters
              isOpen={filtersVisible}
              anchorEl={anchorEl}
              onClose={() => setFiltersVisible(false)}
              filters={filters}
              setFilters={updateSearch}
              registryFilters={false}
            />
          )}
          activeViews={cisoDisplay ? ["list", "card", "map"] : []}
          searchView={searchView}
          setSearchView={setSearchView}
          renderCard={cisoDisplay ? renderCardView : undefined}
          renderMap={cisoDisplay ? renderMapView : undefined}
          onRowClick={(row) => {
            if (cisoDisplay) {
              handleRowClick(row as unknown as SubmissionSummary);
            } else {
              navigate(`/${appName}/${orgId}/report/${row.id}/condensed`);
            }
          }}
          getRowClassName={(params) => {
            if (selectedReport && selectedReport.id === params.id) {
              return style.selectedRow;
            }
            if (compareReport && compareReport.id === params.id) {
              return style.selectedRow;
            }
            return style.reportRow;
          }}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
        />
      }
      onDrawerClose={() => {
        setSelectedReport(null);
        setCompareReport(null);
        setSearchParams((prev) => {
          prev.delete("pinToFront");
          return prev;
        });
        setPinnedSubmission(null);
        closeDrawer();
      }}
    />
  );
};

export default ReportV2List;
